import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { ICartEntry, IOffer } from '@rbi-ctg/menu';
import { useToast } from 'hooks/use-toast';
import { useCRMEventsContext } from 'state/crm-events';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { addLoggingContext } from 'utils/logger';
import { findOfferByUniqId } from 'utils/offers';

import { ClearSelectedOffer, ISelectedOfferPersistedState, IUseSelectedOfferProps } from './types';

const getPreloadedState = () =>
  LocalStorage.getItem<ISelectedOfferPersistedState>(StorageKeys.OFFER) || {};

export default function useSelectedOffer({
  offers,
  lockedOffers,
  cartPromoCodeOffers,
}: IUseSelectedOfferProps) {
  const toast = useToast();
  const {
    selectedOffer: preloadedSelectedOffer = null,
    selectedOfferCartEntry: preloadedSelectedOfferCartEntry = null,
    selectedOfferPrice: preloadedSelectedOfferPrice = 0,
  } = useMemo(getPreloadedState, []);
  const [selectedOffer, setSelectedOffer] = useState<IOffer | null>(preloadedSelectedOffer);
  const [selectedOfferCartEntry, setSelectedOfferCartEntry] = useState<ICartEntry | null>(
    preloadedSelectedOfferCartEntry
  );
  const [selectedOfferPrice, setSelectedOfferPrice] = useState<number>(preloadedSelectedOfferPrice);
  const { logAddOrRemoveFromCart } = useCRMEventsContext();
  const { formatMessage } = useIntl();

  const logOfferRemovedEvents = useCallback(
    (removedEntry: ICartEntry) => {
      logAddOrRemoveFromCart({
        action: 'remove',
        cartEntry: removedEntry,
        previousCartEntries: [],
      });
    },
    [logAddOrRemoveFromCart]
  );

  const logOfferAddedEvents = useCallback(
    (entry: ICartEntry, currentCartEntries: ICartEntry[]) => {
      logAddOrRemoveFromCart({
        action: 'add',
        cartEntry: entry,
        previousCartEntries: currentCartEntries,
      });
    },
    [logAddOrRemoveFromCart]
  );

  const setSelectedOfferCartEntryWithLogging = useCallback(
    (entry: ICartEntry | null, currentCartEntries: ICartEntry[]) => {
      setSelectedOfferCartEntry(entry);
      if (entry) {
        logOfferAddedEvents(entry, currentCartEntries);
      }
    },
    [logOfferAddedEvents]
  );

  const clearSelectedOffer: ClearSelectedOffer = useCallback(
    (args = { doLog: true, popToast: false }) => {
      const { doLog, popToast } = args;

      const prevOfferCartEntry = selectedOfferCartEntry;

      setSelectedOffer(null);
      setSelectedOfferCartEntry(null);
      setSelectedOfferPrice(0);
      if (popToast) {
        toast.show({
          text: formatMessage({ id: 'offerAppliedSuccess' }),
          variant: 'positive',
        });
      }

      if (prevOfferCartEntry && doLog) {
        logOfferRemovedEvents(prevOfferCartEntry);
      }
    },
    [toast, formatMessage, logOfferRemovedEvents, selectedOfferCartEntry]
  );

  const selectOfferById = useCallback(
    (couponId: string) => {
      // Check first if the offer is a locked offer
      const lockedOffer = findOfferByUniqId(couponId, lockedOffers);
      if (lockedOffer) {
        return setSelectedOffer(lockedOffer);
      }
      // Check for cart promo code offers, not included in main offers list
      const cartPromoCodeOffer = findOfferByUniqId(couponId, cartPromoCodeOffers);
      if (cartPromoCodeOffer) {
        return setSelectedOffer(cartPromoCodeOffer);
      }
      // If not a locked offer, check standard offers and promo code offers
      const offer = findOfferByUniqId(couponId, offers);
      if (offer) {
        return setSelectedOffer(offer);
      }
    },
    [lockedOffers, cartPromoCodeOffers, offers]
  );

  const isSelectedOfferCartEntry = useCallback(
    (cartEntry?: ICartEntry | null): boolean =>
      cartEntry?.cartId === selectedOfferCartEntry?.cartId,
    [selectedOfferCartEntry]
  );

  useEffect(() => {
    LocalStorage.setItem(StorageKeys.OFFER, {
      selectedOffer,
      selectedOfferCartEntry,
      selectedOfferPrice,
    });
    addLoggingContext({
      selectedOffer,
      selectedOfferCartEntry,
      selectedOfferPrice,
    });
  }, [selectedOffer, selectedOfferCartEntry, selectedOfferPrice]);

  return {
    clearSelectedOffer,
    isSelectedOfferCartEntry,
    selectOfferById,
    selectedOffer,
    selectedOfferCartEntry,
    selectedOfferPrice,
    setSelectedOfferCartEntry: setSelectedOfferCartEntryWithLogging,
    setSelectedOfferPrice,
  };
}
